import React from 'react'
import { FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CreateWalletDto } from 'api/Wallet/Types/CreateWalletDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { WalletStates } from 'api/Wallet/Types/WalletStates'
import { WalletTypeDto } from 'api/Wallet/Types/WalletTypeDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

const AddWalletFormUI: React.FC<FormikProps<AddWalletFormValues> & OuterProps> = (props) => {
  const {
    availableWalletCurrencies,
    availableWalletCurrenciesLoading,
    walletTypes,
    isSubmitting,
    walletStates,
    errors,
    getAvailableWalletCurrencies,
    setFieldValue,
    values,
  } = props

  const handleSelectWalletType = (value: string | number) => {
    const wallet = walletTypes.find((type) => type.id === Number(value))
    if (wallet) {
      getAvailableWalletCurrencies(wallet.id)
      setFieldValue('walletTypeId', wallet.id)
    }
  }

  if (
    !availableWalletCurrenciesLoading &&
    availableWalletCurrencies.length <= 0 &&
    props.values.walletTypeId
  ) {
    return (
      <DialogContent size='sm'>
        <DialogHeader>
          <DialogTitle>Add Wallet</DialogTitle>
          <DialogDescription>
            The maximum number of wallets have been created, you cannot add more.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => props.onClose()}>OK</Button>
        </DialogFooter>
      </DialogContent>
    )
  }

  return (
    <DialogContent size='sm' render={<Form />}>
      <DialogHeader>
        <DialogTitle>Add Wallet</DialogTitle>
      </DialogHeader>
      <FormSelectField
        name='walletTypeId'
        label='Type'
        value={values.walletTypeId}
        options={walletTypes || []}
        error={errors.walletTypeId}
        allowClear
        onChange={handleSelectWalletType}
      />
      <FormSelectField
        name='currencyId'
        label='Currency'
        value={values.currencyId}
        options={(availableWalletCurrencies || []).map((currency) => ({
          id: currency.id,
          name: currency.id,
        }))}
        error={errors.currencyId}
        allowClear
        onChange={(value) => setFieldValue('currencyId', value)}
      />
      <FormSelectField
        name='walletStateId'
        label='State'
        value={values.walletStateId}
        options={(walletStates || []).map((walletState) => ({
          id: walletState.id,
          name: walletState.name,
          disabled: walletState.id > 1,
        }))}
        error={errors.walletStateId}
        onChange={(value) => setFieldValue('walletStateId', Number(value))}
      />
      <DialogFooter>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

interface OuterProps {
  clientId: string
  walletTypes: WalletTypeDto[]
  availableWalletCurrencies: WalletCurrencyDto[]
  onClose(): void
  availableWalletCurrenciesLoading: boolean
  walletStates: WalletStates[]
  onSubmit(values: FormSubmitValues<CreateWalletDto>): void
  getAvailableWalletCurrencies(walletTypeId: number): Promise<void>
}

type AddWalletFormValues = Omit<CreateWalletDto, 'currencyId'> & { currencyId?: CurrencyType }

function dataToSubmit(values: AddWalletFormValues): FormSubmitValues<CreateWalletDto> {
  return {
    walletTypeId: values.walletTypeId,
    walletStateId: values.walletStateId,
    currencyId: values.currencyId as CurrencyType,
    clientId: values.clientId,
  }
}

export const AddWalletForm = withFormik<OuterProps, AddWalletFormValues>({
  mapPropsToValues: ({ clientId }) => {
    return {
      clientId,
      walletStateId: 1,
      walletTypeId: undefined,
      currencyId: undefined,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddWalletFormValues> = {}
    if (!values.currencyId) {
      errors.currencyId = 'Required'
    }
    if (!values.walletStateId) {
      errors.walletStateId = 'Required'
    }
    if (!values.walletTypeId) {
      errors.walletTypeId = 'Required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddWalletFormUI)
